import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {Banner, Button} from '@primer/react'
import {useState} from 'react'

import type {VerifiableDomainsBannerPayload} from './types'
import styles from './VerifiableDomainsBanner.module.css'

/** Link members follow to fix their organization notification routing email. */
export const NOTIFICATION_SETTINGS_URL = '/settings/notifications#organization_routing'
/** Link members follow to add an email from an eligible domain. */
export const EMAIL_SETTINGS_URL = '/settings/emails'

/** Number of eligible domains shown before the "Show more" toggle collapses the rest. */
const VISIBLE_DOMAIN_COUNT = 3

/**
 * Warning banner shown to organization members who are opted in to email
 * notifications but whose notification email does not belong to one of the
 * organization's verified or approved domains. Because the organization
 * restricts notification delivery to those domains, the member risks missing
 * notifications until they update their email. The server only surfaces this
 * banner when the member is affected.
 */
export function VerifiableDomainsBanner({
  organizationLogin,
  hasEligibleEmail,
  eligibleDomains,
}: VerifiableDomainsBannerPayload) {
  const [expanded, setExpanded] = useState(false)

  const visibleDomains = eligibleDomains.slice(0, VISIBLE_DOMAIN_COUNT)
  const hiddenDomains = eligibleDomains.slice(VISIBLE_DOMAIN_COUNT)

  return (
    <Banner
      actionsLayout="stacked"
      variant="warning"
      flush
      hideTitle
      title={`Update your notification email to keep receiving ${organizationLogin} notifications`}
      description={
        <>
          <p>
            {hasEligibleEmail ? (
              <>
                It looks like you are opted in to receive <strong>{organizationLogin}</strong> email notifications, but
                you have an ineligible email address configured in your notification routing settings. In order to
                receive email notifications for this organization, you&apos;ll have to update your organization routing
                settings for <strong>{organizationLogin}</strong> to use an email from one of the following domains:
              </>
            ) : (
              <>
                It looks like you are opted in to receive <strong>{organizationLogin}</strong> email notifications, but
                haven&apos;t added an email from a verified or approved domain. This organization requires a verified
                email from one of the following domains in order to receive email notifications for organization
                activity:
              </>
            )}
          </p>
          <ul className={styles.DomainList}>
            {visibleDomains.map(domain => (
              <li key={domain}>
                <strong>{domain}</strong>
              </li>
            ))}
            {expanded &&
              hiddenDomains.map(domain => (
                <li key={domain}>
                  <strong>{domain}</strong>
                </li>
              ))}
          </ul>
          {hiddenDomains.length > 0 && (
            <Button
              variant="invisible"
              size="small"
              aria-expanded={expanded}
              trailingVisual={expanded ? ChevronUpIcon : ChevronDownIcon}
              onClick={() => setExpanded(value => !value)}
            >
              {expanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </>
      }
      primaryAction={
        hasEligibleEmail ? (
          <Banner.PrimaryAction as="a" href={NOTIFICATION_SETTINGS_URL}>
            View notification settings
          </Banner.PrimaryAction>
        ) : (
          <Banner.PrimaryAction as="a" href={EMAIL_SETTINGS_URL}>
            Add an email from an eligible domain
          </Banner.PrimaryAction>
        )
      }
    />
  )
}
